import { useEffect, useState } from "react";
import { PencilSimple, AddressBook, Buildings } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import { useCompany } from "../../context/CompanyContext";
import { formatINR } from "../../lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";

const empty = { mis_enabled: false, contract_type: "per_page", rate_per_page: 1.0, fixed_monthly_rate: 0 };

export default function MISEntities() {
  const { active, activeId } = useCompany();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    if (!activeId) { setList([]); return; }
    const { data } = await api.get("/mis/entities", { params: { company_id: activeId } });
    setList(data);
  };

  useEffect(() => { load(); }, [activeId]);

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      mis_enabled: !!c.mis_enabled,
      contract_type: c.contract_type || "per_page",
      rate_per_page: Number(c.rate_per_page || 0),
      fixed_monthly_rate: Number(c.fixed_monthly_rate || 0),
    });
    setOpen(true);
  };

  const save = async () => {
    if (form.mis_enabled && form.contract_type === "per_page" && Number(form.rate_per_page) <= 0) {
      toast.error("Rate per page must be > 0"); return;
    }
    if (form.mis_enabled && form.contract_type === "fixed_monthly" && Number(form.fixed_monthly_rate) <= 0) {
      toast.error("Fixed monthly rate must be > 0"); return;
    }
    const payload = {
      mis_enabled: form.mis_enabled,
      contract_type: form.contract_type,
      rate_per_page: parseFloat(form.rate_per_page || 0),
      fixed_monthly_rate: parseFloat(form.fixed_monthly_rate || 0),
    };
    await api.put(`/mis/entities/${editing.id}/billing`, payload);
    toast.success("Billing updated");
    setOpen(false); load();
  };

  if (!activeId) {
    return (
      <Layout title="MIS Billing Entities">
        <div className="border border-black/10 p-16 text-center">
          <Buildings size={36} className="mx-auto text-[#D90429]"/>
          <h3 className="font-display text-2xl font-bold tracking-tight mt-4">Pick a company first</h3>
          <button onClick={() => navigate("/finance/companies")} className="mt-5 px-4 py-2.5 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C]">Go to Companies</button>
        </div>
      </Layout>
    );
  }

  const enabled = list.filter((c) => c.mis_enabled).length;

  return (
    <Layout title="MIS Billing Entities" subtitle={`${enabled} of ${list.length} clients enabled for MIS in ${active?.name || ""}`}>
      {list.length === 0 ? (
        <div className="border border-black/10 p-16 text-center text-[#717680]">
          <AddressBook size={36} className="mx-auto text-[#D90429]"/>
          <p className="mt-4">No clients in this company yet. Add a client under Finance → Clients & Vendors first.</p>
          <button onClick={() => navigate("/finance/contacts")} className="mt-5 px-4 py-2.5 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C]">Go to Clients</button>
        </div>
      ) : (
        <div className="border border-black/10 bg-white overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]" data-testid="mis-entities-table">
            <thead className="bg-[#F5F6F8] text-left"><tr>{["Entity","MIS","Contract","Rate","Monthly Fee",""].map(h=>(<th key={h} className="px-4 py-3 font-mono-shelv text-xs uppercase tracking-widest text-[#717680]">{h}</th>))}</tr></thead>
            <tbody>
              {list.map((c)=>(
                <tr key={c.id} className="border-t border-black/5 hover:bg-[#FAFBFC]" data-testid={`mis-entity-row-${c.id}`}>
                  <td className="px-4 py-3"><div className="font-semibold">{c.name}</div><div className="text-xs text-[#717680] font-mono-shelv">{c.gstin || ""}</div></td>
                  <td className="px-4 py-3">{c.mis_enabled ? <span className="text-xs font-mono-shelv uppercase tracking-widest bg-[#2A9D8F]/15 text-[#2A9D8F] px-2 py-0.5">Enabled</span> : <span className="text-xs font-mono-shelv uppercase tracking-widest bg-black/5 text-[#717680] px-2 py-0.5">Off</span>}</td>
                  <td className="px-4 py-3 text-[#4A4D57] uppercase font-mono-shelv text-xs">{c.contract_type ? c.contract_type.replace("_"," ") : "—"}</td>
                  <td className="px-4 py-3 font-mono-shelv">{c.contract_type==="per_page" ? `${formatINR(c.rate_per_page)} / page` : "—"}</td>
                  <td className="px-4 py-3 font-mono-shelv">{c.contract_type==="fixed_monthly" ? formatINR(c.fixed_monthly_rate) : "—"}</td>
                  <td className="px-4 py-3 text-right"><button onClick={()=>openEdit(c)} className="p-1.5 hover:bg-black/5" data-testid={`edit-mis-billing-${c.id}`}><PencilSimple size={14}/></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-lg">
          <DialogHeader><DialogTitle className="font-display tracking-tight">MIS billing · {editing?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-between border border-black/10 px-4 py-3">
              <div><div className="font-semibold text-sm">Enable MIS billing</div><div className="text-xs text-[#717680]">Include this client in monthly MIS reports</div></div>
              <Switch checked={form.mis_enabled} onCheckedChange={(v)=>setForm({...form, mis_enabled:v})} data-testid="mis-enable-switch"/>
            </div>
            <div className={form.mis_enabled ? "" : "opacity-50 pointer-events-none"}>
              <Label>Contract Type</Label>
              <Select value={form.contract_type} onValueChange={(v)=>setForm({...form, contract_type:v})}>
                <SelectTrigger className="rounded-none" data-testid="contract-type-select"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="per_page">Per-Page</SelectItem>
                  <SelectItem value="fixed_monthly">Fixed Monthly</SelectItem>
                </SelectContent>
              </Select>
              {form.contract_type === "per_page" ? (
                <div className="mt-3">
                  <Label>Rate per Page (₹)</Label>
                  <Input type="number" step="0.01" value={form.rate_per_page} onChange={(e)=>setForm({...form, rate_per_page:e.target.value})} className="rounded-none" data-testid="rate-per-page-input"/>
                  <p className="text-xs text-[#717680] mt-1">Revenue = QC-passed pages × this rate</p>
                </div>
              ) : (
                <div className="mt-3">
                  <Label>Fixed Monthly Rate (₹)</Label>
                  <Input type="number" value={form.fixed_monthly_rate} onChange={(e)=>setForm({...form, fixed_monthly_rate:e.target.value})} className="rounded-none" data-testid="fixed-monthly-input"/>
                  <p className="text-xs text-[#717680] mt-1">Charged once per month when there is any activity logged</p>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <button onClick={()=>setOpen(false)} className="px-4 py-2 border border-black/15 text-sm">Cancel</button>
            <button onClick={save} data-testid="save-mis-billing-button" className="px-4 py-2 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C]">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
